<template>
	<div>
		<div v-for="item in MenuItems" :key="item.ObjectType" >
			<div v-if="item.Visible">
				<!-- Item sans sous-menu  -->
				<hsc-menu-item v-if="item.SubItems && item.SubItems.length == 0" :label="item.Text" >
					<div slot="body" v-if="item.Icon" @click="checkExistingVisible(item)">
						<b-img :src="AbsoluteLink('Icons/' + item.Icon)" />&nbsp;&nbsp;{{ item.Text }} 
					</div>
				</hsc-menu-item>
				<!-- Item avec sous-menu (seulement s'il contient des items enfants visibles) -->
				<hsc-menu-item v-else :label="item.Text">
					<div slot="body" v-if="item.Icon">
						<b-img :src="AbsoluteLink('Icons/' + item.Icon)" />&nbsp;&nbsp;{{ item.Text }} 
					</div>
					<!-- v-on="$listeners" permet de diffuser les évènements d'ObjectMenuItem aux composants parents d'ObjectMenuMenuItem 
						(en particulier CloseObjectMenuEvent pour fermeture du menu) -->
					<object-menu-item :MenuItems="item.SubItems" :ParentNode="ParentNode" v-on="$listeners" />
				</hsc-menu-item>
			</div>
		</div>

		<ExistingEquipment v-if="existingVisible" :Visible.sync="existingVisible" :ExistingEquipments="existingEquipments" :SelectedMenuItem="selectedMenuItem" :ParentNode="ParentNode" @CreateNewEquipmentEvent="loadPropertyObject" />
	</div>
</template>

<script lang="ts">
	import Component, { mixins } from 'vue-class-component';
	import { Prop, Vue } from "vue-property-decorator";
	import { API_URL, MAIN_FILTER_TREE } from "@/App.settings";
	import axios from "axios";
	import Dictionary from "@/types/Dictionary";
	import BaseObjectTypeEnum from "@/enums/BaseObjectTypeEnum";
	import MenuItem from "@/types/MenuItem";
	import { LinkUtils } from "@/components/commons/Utils";
	import locales from '../locales/ObjectMenuItem.json';
	import ExistingEquipment from './ExistingEquipment.vue';
	import EnumItem from '@/types/fields/EnumItem';
	import TreeNode from '@/types/TreeNode';
	
	/** 
	 * Item de menu de création d'objet
	 * @requires ObjectMenu Composant de menu de création d'objet
	 *  */
	@Component({
		name: 'object-menu-item',
		components: {
			ObjectMenuItem,
			ExistingEquipment
		},
		i18n: {messages: locales}
	})
	export default class ObjectMenuItem extends mixins(LinkUtils) {
		/** Nécessaire pour pouvoir utiliser cet enum dans la section template */
		private baseObjectTypeEnum = BaseObjectTypeEnum;

		@Prop()
		public MenuItems!: MenuItem[];

		/** NodeId parent du nouveau noeud à créer */
		@Prop()
		public ParentNode!: TreeNode;

		/** Permet d'indiquer si la fenêtre de sélection de création d'un equipement existant est affiché ou non */
		private existingVisible : boolean = false;		

		/** Element sélectionné dans le menu*/
		private selectedMenuItem : MenuItem;

		/** Liste des équipements existants */
		private existingEquipments: EnumItem[] = [];

		/** Méthode pour savoir si l'objet à ajouter peut être fantôme selon le type d'objet */
		private async checkExistingVisible(item: MenuItem) {
			if (item.PrimaryType == BaseObjectTypeEnum.Equipment) {
				const args: Dictionary<any> = {};
				args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
				args["ObjectType"] = item.ObjectType;
				args["ParentId"] = this.ParentNode.Id;

				await axios.post(API_URL + "object/Equipment/GetPossibleGhost", args).then((response) => {
					console.debug("ObjectMenuItem - CheckExistingVisible - item : %O - response : %O", item, response);
					if (response.data['possibleGhost'] && response.data['existingEquipments'].length > 0) {
						console.debug("ObjectMenuItem - CheckExistingVisible - getPossibleGhost")
						// Affichage modale création equipement existant
						this.existingVisible = true;
						this.selectedMenuItem = item;
						this.existingEquipments = response.data['existingEquipments'];
					}
					else
						this.loadPropertyObject(item.ObjectType);
				});
			}
			else if(item.PrimaryType == BaseObjectTypeEnum.BusinessObject)
				this.createBusinessObject(item.ObjectType);
			else
				this.loadPropertyObject(item.ObjectType);
		}

		/** Création d'un objet métier */
		private createBusinessObject(newObjectType: string) {
			const args: Dictionary<any> = {};
			args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
			args["ParentId"] = this.ParentNode.Id;
			args["ObjectType"] = newObjectType;

			axios.post(API_URL + "/object/BO/New", args).then((response) => {
				if (response && response.data) {
					// Rafraichit l'arborescence et ouvre le noeud parent
					/** @todo Obligé de mettre à jour complètement l'arborescence pour avoir les bons LeftBound/RightBound  ==> voir si nécessaire d'optimiser avec SLavina */
					this.$store.dispatch(MAIN_FILTER_TREE + '/Refresh', { nodeIdToOpen: this.ParentNode.Id });

					this.$bvToast.toast(this.$t('BusinessObjectSuccefullyAdded').toString(), { // $t('BusinessObjectSuccefullyAdded') / L'objet Métier a été ajouté avec succès.
						title: this.$t('AddBusinessObject').toString(), // $t('AddBusinessObject') / Ajout objet métier
						autoHideDelay: 3000,
						variant: "success",
						solid: true,
						appendToast: true,
						toaster: "b-toaster-top-center"
					});
				}
			})
			.catch(err => {
				console.error(err);
				this.$bvToast.toast(this.$t('AnErrorOccured').toString(), { // $t('AnErrorOccured') / Une erreur s'est produite.
					title:  this.$t('Error').toString(), // $t('Error') / Erreur
					autoHideDelay: 10000,
					variant: "danger",
					solid: true,
					appendToast: true,
					toaster: "b-toaster-top-center"
				});
			});

			// Emet évènement de fermeture du menu après sélection (géré au niveau du composant dans lequel est affiché le menu)
			this.$emit('CloseObjectMenuEvent');
		}

		/** Création d'un nouvel objet pour un type donné */
		private async loadPropertyObject(newTypeObject: string) {
			console.debug('loadPropertyObject - Début new : %O', newTypeObject);

			await this.$store.dispatch("ObjectStore/New", { objectType: newTypeObject, parentNode: this.ParentNode }).then(() => {
				console.debug('loadPropertyObject - new réussi !');
			})
			.catch(err => {
				console.error(err);
				this.$bvToast.toast(this.$t('AnErrorOccured').toString(), { // Une erreur s'est produite.
					title: this.$t('Error').toString(), // Erreur
						autoHideDelay: 10000,
						variant: "danger",
						solid: true,
						appendToast: true,
						toaster: "b-toaster-top-center"
					});
			});

			// Emet évènement de fermeture du menu après sélection (géré au niveau du composant dans lequel est affiché le menu)
			this.$emit('CloseObjectMenuEvent');
		}
	}
</script>

<style scoped>
</style>